// React 19 removed several legacy APIs from `react-dom`'s root export:
//   - findDOMNode  (antd 4's rc-trigger / rc-tooltip use it for popup positioning)
//   - render / unmountComponentAtNode  (rc-util's React/render helper used by
//     antd's static message/notification APIs reads these off react-dom)
//   - createRoot / hydrateRoot  (moved to `react-dom/client` only; rc-util's
//     modernRender branch looks for `createRoot` on `react-dom` and falls back
//     to the now-missing legacy `render` when it's absent)
//
// This polyfill re-attaches the missing names onto react-dom's module.exports
// so antd 4 + its rc-* deps keep working until the antd v4 → v5 migration is
// done (tracked separately; see react-19-upgrade-plan.xlsx).
//
// Must use CommonJS require: the ESM namespace from `import * as` is frozen,
// and react-dom@19 doesn't statically export these names so webpack rejects an
// ESM import of them. CommonJS require returns the mutable module.exports
// object that the compiled antd / rc-* code also reads through at runtime.
const ReactDOM = require("react-dom");
const ReactDOMClient = require("react-dom/client");

if (typeof ReactDOM.findDOMNode !== "function") {
  const findFirstHostNode = (fiber) => {
    let node = fiber;
    while (node) {
      if (node.stateNode instanceof Element) return node.stateNode;
      if (node.child) {
        node = node.child;
        continue;
      }
      // Backtrack to the next sibling, but never climb above the start fiber —
      // a host-less subtree must return null, not escape into a sibling/ancestor subtree.
      while (node && node !== fiber && !node.sibling) node = node.return;
      if (!node || node === fiber) return null;
      node = node.sibling;
    }
    return null;
  };

  ReactDOM.findDOMNode = (instance) => {
    if (instance == null) return null;
    if (instance instanceof Element) return instance;
    if (instance.nodeType != null) return instance;
    const fiberKey = Object.keys(instance).find((k) => k.startsWith("__reactFiber$"));
    const fiber = instance._reactInternals || instance._reactInternalFiber || (fiberKey ? instance[fiberKey] : null);
    return fiber ? findFirstHostNode(fiber) : null;
  };
}

// rc-util's React/render helper reads `createRoot` / `hydrateRoot` off the
// `react-dom` root export. In React 19 those moved to `react-dom/client` only,
// so re-attach them. With createRoot present, rc-util takes the modernRender
// branch and never calls the legacy `render` — but we still shim `render` /
// `unmountComponentAtNode` defensively, so any other consumer also works.
const rootRegistry = new WeakMap();

if (typeof ReactDOM.createRoot !== "function") {
  ReactDOM.createRoot = ReactDOMClient.createRoot;
}
if (typeof ReactDOM.hydrateRoot !== "function") {
  ReactDOM.hydrateRoot = ReactDOMClient.hydrateRoot;
}
/* eslint-disable react/no-deprecated -- intentional polyfill of legacy React APIs */
if (typeof ReactDOM.render !== "function") {
  // NOSONAR — intentional polyfill of deprecated React 17 API
  ReactDOM.render = (node, container, callback) => {
    // NOSONAR
    let root = rootRegistry.get(container);
    if (!root) {
      root = ReactDOMClient.createRoot(container);
      rootRegistry.set(container, root);
    }
    root.render(node);
    if (typeof callback === "function") queueMicrotask(callback);
    return null;
  };
}
if (typeof ReactDOM.unmountComponentAtNode !== "function") {
  // NOSONAR — intentional polyfill of deprecated React 17 API
  ReactDOM.unmountComponentAtNode = (container) => {
    // NOSONAR
    const root = rootRegistry.get(container);
    if (root) {
      root.unmount();
      rootRegistry.delete(container);
      return true;
    }
    return false;
  };
}
/* eslint-enable react/no-deprecated */
